import { useEffect, useMemo, useState } from 'react';
import InstagramIcon from '@mui/icons-material/Instagram';
import { Box, Button, Card, CircularProgress, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Config } from 'src/config';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';

export default function InstagramRedirect() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Completing Instagram connection...');

  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const code = useMemo(() => query.get('code') || '', [query]);
  const error = useMemo(() => query.get('error_description') || query.get('error') || '', [query]);
  const oauthState = useMemo(() => CommonHelper.ParseMetaOAuthState(query.get('state') || ''), [query]);

  useEffect(() => {
    const storageKey = 'brand2chat_instagram_oauth_result';
    const buildMessage = (authCode, authError = '') => ({
      type: 'brand2chat:meta-oauth-code',
      service: 'instagram',
      code: authCode || '',
      error: authError || '',
      redirect_url: `${window.location.origin}/instagram-redirect`,
      mode: oauthState?.mode || window.localStorage.getItem('instagram_oauth_mode') || 'connect',
      instagram_id: oauthState?.instagram_id || window.localStorage.getItem('instagram_oauth_instagram_id') || oauthState?.account_id || window.localStorage.getItem('instagram_oauth_account_id') || '',
    });
    const postMessageToApp = (payload) => {
      const targetOrigin = oauthState?.source_origin || '*';
      if (window.opener) {
        window.opener.postMessage(payload, targetOrigin);
      }
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(payload, targetOrigin);
      }
    };

    if (oauthState?.use_oauth_bridge && (code || error)) {
      const payload = buildMessage(code, error);
      window.localStorage.setItem(storageKey, JSON.stringify(payload));
      postMessageToApp(payload);
      setStatus(error ? 'error' : 'success');
      setMessage(error || 'Instagram authorization received. Returning to your app...');
      window.setTimeout(() => window.close(), 500);
      return undefined;
    }

    if (error) {
      setStatus('error');
      setMessage(error);
      return undefined;
    }

    if (!code) {
      const interval = window.setInterval(() => {
        const rawPayload = window.localStorage.getItem(storageKey);
        if (!rawPayload) return;
        try {
          const payload = JSON.parse(rawPayload);
          postMessageToApp(payload);
          window.localStorage.removeItem(storageKey);
        } catch (_err) {
          window.localStorage.removeItem(storageKey);
        }
      }, 1000);
      return () => window.clearInterval(interval);
    }

    const redirectURL =
      oauthState?.redirect_url ||
      window.localStorage.getItem('instagram_oauth_redirect_uri') ||
      Config.Instagram.redirectUri;
    const instagramID =
      oauthState?.instagram_id ||
      oauthState?.account_id ||
      window.localStorage.getItem('instagram_oauth_instagram_id') ||
      window.localStorage.getItem('instagram_oauth_account_id') ||
      '';
    const clearOAuthStorage = () => {
      window.localStorage.removeItem('instagram_oauth_redirect_uri');
      window.localStorage.removeItem('instagram_oauth_mode');
      window.localStorage.removeItem('instagram_oauth_account_id');
      window.localStorage.removeItem('instagram_oauth_instagram_id');
    };

    if (instagramID) {
      HttpServices.Post({
        url: userModuleURL.instagramReauthenticate,
        body: {
          code,
          instagram_id: instagramID,
          redirect_url: redirectURL,
        },
        successCallback: () => {
          clearOAuthStorage();
          setStatus('success');
          setMessage('Instagram account re-authenticated successfully.');
          window.setTimeout(() => navigate('/integrations?tab=accounts', { replace: true }), 700);
        },
        failureCallback: (errorMessage) => {
          clearOAuthStorage();
          setStatus('error');
          setMessage(errorMessage || 'Instagram account re-authentication failed.');
        },
      });
      return;
    }

    HttpServices.Post({
      url: userModuleURL.instagramAuth,
      body: {
        code,
        redirect_url: redirectURL,
      },
      successCallback: (data) => {
        clearOAuthStorage();
        window.sessionStorage.setItem('instagram_integration_accounts', JSON.stringify(data || {}));
        setStatus('success');
        setMessage('Instagram accounts loaded. Choose the accounts you want to integrate.');
        window.setTimeout(() => navigate('/integrations?instagram_accounts=1', { replace: true }), 700);
      },
      failureCallback: (errorMessage) => {
        clearOAuthStorage();
        setStatus('error');
        setMessage(errorMessage || 'Instagram connection failed.');
      },
    });
    return undefined;
  }, [code, error, navigate, oauthState]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#f8fafc', p: 2 }}>
      <Card variant="outlined" sx={{ width: '100%', maxWidth: 460, p: 3, borderRadius: 1 }}>
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          {status === 'loading' ? (
            <CircularProgress />
          ) : (
            <InstagramIcon color={status === 'success' ? 'primary' : 'error'} sx={{ fontSize: 44 }} />
          )}
          <Typography variant="h5" fontWeight={800}>
            Instagram Integration
          </Typography>
          <Typography color="text.secondary">{message}</Typography>
          <Button component={RouterLink} to="/integrations" variant="contained">
            Back to integrations
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}
